import * as React from "react"
import { Link } from "react-router-dom"

import { cn } from "@/lib/utils"
import { toneClass, TONE_TEXT_CLASS } from "@/lib/status"

/*
  PageHeader — the top of every page:
    Projects (63)
    45 active projects across Dubai and Abu Dhabi · ● 1 past end date · ● 16 stages awaiting approval
                                                                   [secondary]  [■ Primary]

    <PageHeader
      title="Projects"
      count={63}
      subtitle="45 active projects across Dubai and Abu Dhabi"
      alerts={[
        { label: '1 past end date', tone: 'red', onClick: () => setFilter('late') },
        { label: '16 stages awaiting approval', tone: 'amber', to: '/projects?stage=awaiting' },
      ]}
      actions={<><Button variant="outline">Export</Button><Button>New project</Button></>}
    />

  Props: title, count (pill after the title; number or text), subtitle (node), alerts
  [{ label, tone 'red'|'amber'|'blue'|'green'|'navy', onClick | to | href, key }], actions (node, right side —
  at most one primary button), titleAdornment (node after the count, e.g. a StatusPill), as ('h1'), className.
  Below 640px the actions sit under the title block (full width); pages need no layout overrides.
*/
function AlertLink({ alert }) {
  const tone = alert.tone || "amber"
  const content = (
    <>
      <span className={cn("ui-tone-dot h-[7px] w-[7px]", toneClass(tone))} aria-hidden="true" />
      {alert.label}
    </>
  )
  const classes = cn(
    "inline-flex items-center gap-[5px] whitespace-nowrap text-[12.5px] font-semibold",
    TONE_TEXT_CLASS[tone] || TONE_TEXT_CLASS.amber,
    (alert.onClick || alert.to || alert.href) && "rounded hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
  )
  if (alert.to) return <Link to={alert.to} className={classes} onClick={alert.onClick}>{content}</Link>
  if (alert.href) return <a href={alert.href} className={classes} onClick={alert.onClick}>{content}</a>
  if (alert.onClick) return <button type="button" className={classes} onClick={alert.onClick}>{content}</button>
  return <span className={classes}>{content}</span>
}

/*
  Subtitle line: every item carries its own " · " in front, kept on the same line as the item
  (whitespace-nowrap), so a wrapped line never ends with a hanging "·". The row is pulled left by the
  separator's width and clipped on the left only, so the item that STARTS a line hides its "·"
  (no orphan separator at the start of a wrapped line either). Focus rings stay visible.
*/
const SEPARATOR_WIDTH = "w-[18px]"

function SubtitleItem({ children, className }) {
  return (
    <span className={cn("inline-flex min-w-0 items-center", className)}>
      <span className={cn("inline-block shrink-0 text-center text-ink-4", SEPARATOR_WIDTH)} aria-hidden="true">·</span>
      {children}
    </span>
  )
}

const PageHeader = React.forwardRef(({
  title,
  count,
  subtitle,
  alerts,
  actions,
  titleAdornment,
  as: TitleTag = "h1",
  className,
  children,
  ...props
}, ref) => {
  const visibleAlerts = (alerts || []).filter(Boolean)
  const hasCount = count !== undefined && count !== null && count !== ""

  return (
    <div
      ref={ref}
      // Phones: actions go under the title (full width) instead of squeezing the subtitle into a column.
      className={cn("flex flex-col items-stretch gap-x-4 gap-y-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between", className)}
      {...props}
    >
      <div className="min-w-0 sm:flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-2.5">
          <TitleTag className="font-heading text-[26px] font-black leading-[1.15] tracking-[-0.02em] text-ink">
            {title}
          </TitleTag>
          {hasCount ? (
            <span
              className="rounded-full bg-[color:var(--status-gray-bg)] px-[9px] py-0.5 text-[13px] font-bold text-ink-3 tabular-nums"
              data-testid="page-header-count"
            >
              {count}
            </span>
          ) : null}
          {titleAdornment}
        </div>
        {subtitle || visibleAlerts.length ? (
          <div className="mt-1 [clip-path:inset(-100vmax_-100vmax_-100vmax_-3px)]">
            <div className="-ml-[18px] flex min-w-0 flex-wrap items-center gap-y-1 text-[13.5px] text-ink-3">
              {subtitle ? <SubtitleItem className="min-w-0"><span className="min-w-0">{subtitle}</span></SubtitleItem> : null}
              {visibleAlerts.map((alert, index) => (
                <SubtitleItem key={alert.key || alert.label || index} className="whitespace-nowrap">
                  <AlertLink alert={alert} />
                </SubtitleItem>
              ))}
            </div>
          </div>
        ) : null}
        {children}
      </div>
      {actions ? <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{actions}</div> : null}
    </div>
  )
})
PageHeader.displayName = "PageHeader"

export { PageHeader }
